import React from 'react';

import { Box, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { format } from 'date-fns';

import { SmsMessageType } from '../types';
import { SenderTypeEnum, SmsStatusEnum } from '@libs/graphql-types';

interface SmsMessageBubbleProps {
  message: SmsMessageType;
}

const SmsMessageBubble: React.FC<SmsMessageBubbleProps> = ({ message }) => {
  const isSentByUser = message.sender_type === SenderTypeEnum.User;

  const getStatusIcon = () => {
    if (message.status === SmsStatusEnum.Sent) {
      return <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />;
    }
    if (message.status === SmsStatusEnum.Failed) {
      return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    }
    return <ScheduleIcon sx={{ fontSize: 16, color: 'info.main' }} />;
  };

  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'HH:mm');
    } catch {
      return '';
    }
  };

  if (isSentByUser) {
    // Sent message (right) - from company user
    return (
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="flex-end"
        mb={1.5}
      >
        <Box
          maxWidth="60%"
          bgcolor="#E3F2FD"
          borderRadius={2}
          p={2}
          boxShadow={0}
          border="1px solid #BBDEFB"
        >
          <Typography fontSize={15} color="text.primary" whiteSpace="pre-line">
            {message.content || ''}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="flex-end" mt={1} gap={0.5}>
            <Typography fontSize={12} color="text.secondary">
              {formatTime(message.created_at)}
            </Typography>
            {getStatusIcon()}
          </Box>
          {message.status === SmsStatusEnum.Failed && message.error_message && (
            <Chip
              label={message.error_message}
              size="small"
              color="error"
              sx={{ mt: 1, height: 20 }}
            />
          )}
        </Box>
      </Box>
    );
  }

  // Received message (left) - from talent
  return (
    <Box
      display="flex"
      alignItems="flex-end"
      justifyContent="flex-start"
      mb={1.5}
    >
      <Box
        maxWidth="60%"
        bgcolor="#fff"
        borderRadius={2}
        p={2}
        boxShadow={0}
        border="1px solid #E0E0E0"
      >
        <Typography fontSize={15} color="text.primary" whiteSpace="pre-line">
          {message.content || ''}
        </Typography>
        <Typography fontSize={12} color="text.secondary" mt={1}>
          {formatTime(message.created_at)}
        </Typography>
      </Box>
    </Box>
  );
};

export default SmsMessageBubble;
